import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet, apiPost } from '../../api/client';
import AuthedImage from '../../components/AuthedImage';
import { STATUS_CLASS, STATUS_LABEL, type Receipt } from '../../types';
import { fmtDate, fmtTL } from '../../format';

interface Resp {
  user: { id: number; email: string; name: string };
  receipts: Receipt[];
}



export default function UserReceipts() {
  const { id, month: urlMonth } = useParams<{ id: string; month: string }>();
  const [data, setData] = useState<Resp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Receipt | null>(null);

  function load() {
    setData(null);
    setErr(null);
    
    let qs = '';
    if (urlMonth) {
      const [y, m] = urlMonth.split('-');
      if (y && m) {
        qs = `?year=${y}&month=${m}`;
      }
    }

    apiGet<Resp>(`/api/admin/users/${id}/receipts${qs}`)
      .then(setData)
      .catch((e: Error) => setErr(e.message));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, urlMonth]);

  async function review(r: Receipt, status: 'approved' | 'rejected', note: string) {
    try {
      await apiPost(`/api/admin/receipts/${r.id}/review`, { status, admin_note: note || null });
      setSelected(null);
      load();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  return (
    <div>
      <div className="mb-4">
        <Link to={`/yonetim/donemler/${urlMonth}`} className="text-sm text-brand-600 hover:underline">
          ← Kullanıcılara Dön
        </Link>
      </div>
      <h1 className="text-2xl font-bold mb-1">{data?.user.name ?? 'Kullanıcı'} (Dönem Fişleri)</h1>
      <div className="text-slate-500 text-sm mb-6">{data?.user.email}</div>

      {err && <div className="text-red-600 mb-4">{err}</div>}
      {!data ? (
        <div className="text-slate-500">Yükleniyor…</div>
      ) : data.receipts.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">Bu dönemde fiş bulunamadı.</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">Tarih</th>
                <th className="text-left px-4 py-2">Tür</th>
                <th className="text-left px-4 py-2">Şirket</th>
                <th className="text-right px-4 py-2">Net</th>
                <th className="text-right px-4 py-2">KDV</th>
                <th className="text-right px-4 py-2">Toplam</th>
                <th className="text-left px-4 py-2">Durum</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.receipts.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-2">{fmtDate(r.receipt_date)}</td>
                  <td className="px-4 py-2">{r.receipt_type}</td>
                  <td className="px-4 py-2">{r.company_name}</td>
                  <td className="px-4 py-2 text-right">{fmtTL(r.amount_net)}</td>
                  <td className="px-4 py-2 text-right">%{r.vat_rate}</td>
                  <td className="px-4 py-2 text-right font-semibold">{fmtTL(r.total_gross)}</td>
                  <td className="px-4 py-2">
                    <span className={STATUS_CLASS[r.status]}>{STATUS_LABEL[r.status]}</span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button className="btn-secondary" onClick={() => setSelected(r)}>
                      İncele
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <ReviewModal receipt={selected} onClose={() => setSelected(null)} onReview={review} />
      )}
    </div>
  );
}

function ReviewModal({
  receipt,
  onClose,
  onReview,
}: {
  receipt: Receipt;
  onClose: () => void;
  onReview: (r: Receipt, status: 'approved' | 'rejected', note: string) => void;
}) {
  const [note, setNote] = useState(receipt.admin_note ?? '');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold">Fiş Detayı</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">
            ×
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-6 p-6 overflow-y-auto">
          <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-center">
            <AuthedImage src={receipt.image_url} className="max-h-[60vh] w-auto rounded" />
          </div>
          <div className="space-y-3 text-sm">
            <Row k="Tür" v={receipt.receipt_type} />
            <Row k="Şirket" v={receipt.company_name} />
            <Row k="Tarih" v={fmtDate(receipt.receipt_date)} />
            <Row k="Tutar (Net)" v={fmtTL(receipt.amount_net)} />
            <Row k="KDV Oranı" v={`%${receipt.vat_rate}`} />
            <Row k="KDV Tutarı" v={fmtTL(receipt.vat_amount)} />
            <Row k="Toplam (Brüt)" v={fmtTL(receipt.total_gross)} strong />
            <Row
              k="Durum"
              v={<span className={STATUS_CLASS[receipt.status]}>{STATUS_LABEL[receipt.status]}</span>}
            />
            {receipt.employee_note && (
              <div className="bg-amber-50 border border-amber-100 p-2 rounded text-amber-900">
                <div className="font-semibold text-xs uppercase mb-1">Çalışan Notu:</div>
                {receipt.employee_note}
              </div>
            )}
            <div>
              <label className="label">Yönetici Notu (opsiyonel)</label>
              <textarea
                className="input min-h-[90px]"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Onay/red gerekçesi veya not"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button className="btn-secondary" onClick={onClose}>Kapat</button>
          <button className="btn-danger" onClick={() => onReview(receipt, 'rejected', note)}>
            Reddet
          </button>
          <button className="btn-success" onClick={() => onReview(receipt, 'approved', note)}>
            Onayla
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, strong }: { k: string; v: React.ReactNode; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 pb-2">
      <span className="text-slate-500">{k}</span>
      <span className={strong ? 'font-semibold text-brand-700' : 'font-medium'}>{v}</span>
    </div>
  );
}
